import React from "react";

// Shared visual building blocks — Integrated Wellness sibling-product theme.
// Sharp corners, Orbitron display headings, monospace caps labels, navy + red.

const IW_SHIELD =
  "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/amgkh1mp_logo.png";

export const Aurora = ({ children, className = "" }) => (
  <div className={`bg-iw-grid min-h-screen relative ${className}`}>{children}</div>
);

export const Stepper = ({ steps, current }) => (
  <div className="flex items-center gap-2" data-testid="flow-stepper">
    {steps.map((label, i) => (
      <div key={label} className="flex items-center gap-2">
        <div
          className={`step-dot ${
            i === current ? "active" : i < current ? "done" : ""
          }`}
          title={label}
        />
        {i < steps.length - 1 && (
          <div
            className="h-px w-6"
            style={{
              background:
                i < current
                  ? "rgba(30, 58, 138, 0.55)"
                  : "var(--iw-border)",
            }}
          />
        )}
      </div>
    ))}
  </div>
);

export const Card = ({ children, className = "", strong = false, divider = false, ...rest }) => (
  <div
    className={`${strong ? "glass-strong" : "iw-card"} ${divider ? "iw-card-divider" : ""} p-7 sm:p-8 ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export const StopButton = ({ onClick, label = "STOP" }) => (
  <button
    type="button"
    className="stop-btn"
    onClick={onClick}
    data-testid="stop-session-btn"
    aria-label="Stop session immediately"
  >
    ■ {label}
  </button>
);

/**
 * PageHeader — IW house style.
 * "§ 0X — LABEL" (red mono caps) above a huge Orbitron display heading,
 * followed by a regular-weight body subtitle.
 */
export const PageHeader = ({ eyebrow, title, subtitle, animate = true, section }) => (
  <div className={`mb-12 ${animate ? "fade-up" : ""}`}>
    {(section || eyebrow) && (
      <div className="iw-section-head__label mb-3" data-testid="page-header-eyebrow">
        {section ? `§ ${section} — ${eyebrow}` : eyebrow}
      </div>
    )}
    <h1 className="serif" style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}>
      {title}
    </h1>
    {subtitle && (
      <p className="mt-5 text-lg sm:text-xl max-w-2xl leading-relaxed" style={{ color: "var(--iw-body)" }}>
        {subtitle}
      </p>
    )}
  </div>
);

export const Stat = ({ label, value, accent = false }) => (
  <div className="flex flex-col">
    <div className="iw-section-head__label mb-1.5" style={{ color: "var(--iw-muted)" }}>
      {label}
    </div>
    <div
      className={`iw-mono text-3xl sm:text-4xl font-medium ${
        accent ? "text-red" : "text-ink"
      }`}
      style={{ letterSpacing: "0.02em" }}
    >
      {value}
    </div>
  </div>
);

const LOGO_SIZE_MAP = {
  xl: { dims: 80, titleClass: "text-2xl sm:text-3xl", subClass: "text-[11px]" },
  lg: { dims: 64, titleClass: "text-xl sm:text-2xl", subClass: "text-[10px]" },
  md: { dims: 48, titleClass: "text-lg sm:text-xl", subClass: "text-[10px]" },
  sm: { dims: 36, titleClass: "text-base", subClass: "text-[9px]" },
};

export const Logo = ({ size = "md" }) => {
  const { dims, titleClass, subClass } = LOGO_SIZE_MAP[size] || LOGO_SIZE_MAP.md;
  return (
    <div className="flex items-center gap-3 sm:gap-4" data-testid="brand-logo">
      <img
        src={IW_SHIELD}
        alt="Integrated Wellness"
        width={dims}
        height={dims}
        className="flex-shrink-0"
        style={{ filter: "drop-shadow(0 4px 14px rgba(17, 41, 107, 0.25))" }}
      />
      <div className="leading-tight">
        <div className={`serif ${titleClass} font-bold`} style={{ color: "var(--iw-primary)" }}>
          Integrated Wellness
        </div>
        <div
          className={`iw-mono ${subClass} tracking-[0.24em] uppercase mt-1`}
          style={{ color: "var(--iw-accent-red)", fontWeight: 600 }}
        >
          Nervous System Reset
        </div>
      </div>
    </div>
  );
};

// Patent-pending badge — sharp red rectangle in IW style.
export const PatentPendingBadge = ({ className = "" }) => (
  <span
    className={`inline-flex items-center gap-2.5 px-4 py-2 ${className}`}
    style={{
      background: "var(--iw-accent-red)",
      color: "#FFFFFF",
      borderRadius: "2px",
      boxShadow: "0 8px 22px rgba(217, 4, 41, 0.25)",
    }}
    data-testid="patent-pending-badge"
  >
    <span
      className="w-2 h-2 rounded-full flex-shrink-0"
      style={{
        background: "#FFFFFF",
        boxShadow: "0 0 12px rgba(255,255,255,0.85)",
      }}
    />
    <span
      className="iw-mono uppercase"
      style={{
        fontSize: "11px",
        letterSpacing: "0.18em",
        fontWeight: 700,
      }}
    >
      Patent Pending · Bilateral Stimulation
    </span>
  </span>
);

// Large brand mark for the hero (IW shield)
export const BrandMark = ({ size = 220 }) => (
  <img
    src={IW_SHIELD}
    alt="Integrated Wellness"
    width={size}
    height={size}
    style={{ filter: "drop-shadow(0 10px 30px rgba(17, 41, 107, 0.25))" }}
  />
);

/**
 * NowBookingPill — red dot + mono-caps label, mirrors IW's "NOW BOOKING NEW MEMBERS".
 */
export const NowBookingPill = ({ label = "Now Open · Self-Regulation Console" }) => (
  <div className="inline-flex items-center gap-2.5">
    <span className="iw-now-dot" />
    <span
      className="iw-mono uppercase"
      style={{
        fontSize: "11px",
        letterSpacing: "0.22em",
        color: "var(--iw-primary)",
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  </div>
);
